import { useTranslation } from 'react-i18next';
import SearchBar from './SearchBar';
import Pagination from '@shared/components/Pagination';
import PageSizeSelector from './PageSizeSelector';
import PreviousPage from './PreviousPage';

// Helper function to format dates in Bulgarian format
const formatDate = (value) => {
    if (!value) return '';

    // Try to create a date object
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
    }

    return value;
};

// Helper function to check if a value is a date
const isDateValue = (value) => {
    if (!value) return false;

    // If it's already a Date object
    if (value instanceof Date) {
        return !isNaN(value.getTime());
    }

    // Convert to string
    const stringValue = String(value).trim();

    // Simple check: if it looks like a date (contains dashes or slashes with numbers)
    if (/^\d{4}[-/]\d{1,2}[-/]\d{1,2}/.test(stringValue)) {
        const date = new Date(stringValue);
        return !isNaN(date.getTime());
    }

    return false;
};

export default function Table({
    // Data and state
    data,
    loading,
    error,
    selectedData,
    pageNumber,
    pageSize,
    sortBy,
    sortDir,
    totalElements,
    totalPages,
    searchQuery,
    searchType,
    searchOptions,

    // Column configuration
    columns,

    // Title and actions
    title,
    actionButtons,
    employeeInfo = false,
    employeeEmpId = '',
    employeeFullName = '',
    employeeContractName = '',

    // Handlers
    handleSort,
    handleCheckboxChange,
    handleRowClick,
    handleSearchClick,
    setSearchQuery,
    setSearchType,
    setPageSize,
    setpageNumber,
    getData,

    // Checkbox functionality (optional)
    showCheckbox = false,
    checkboxField = null, // New prop for custom checkbox field

    // Select icon functionality (optional)
    showSelectIcon = false,
    selectIconField = null, // Field to use for selection comparison
    selectedRecord = null, // Currently selected record
    onSelectRecord = null, // Handler for record selection
    selectIconTitle = 'Select', // Title for select icon

    // Delete icon functionality (optional)
    showDeleteIcon = false,
    shouldShowDeleteIcon = null,
    onDeleteIconClick = null,

    // Breadcrumb navigation (optional)
    showBreadcrumb = false,
    breadcrumbPath = [],
    onBreadcrumbClick = null,

    // Custom renderers
    renderCell,
    renderStatusBadge,

    // Optional search structure
    showCustomSearch,
    customSearchComponent,
    showPagination = true,
    showPageSizeSelector = true,

    // Company selection props
    showCompanySelect = false,
    companySelectLabel = null,
    showLevelSelect = false,
    showPreviousPage = true,
    showActiveSelect = false,
    showAdditionalContractSelect = false,
    showTerminatedSelect = false,
    companyOptions = [],
    selectedCompany,
    setSelectedCompany,
    selectedActive,
    setSelectedActive,
    selectedAdditionalContract,
    setSelectedAdditionalContract,
    selectedTerminated,
    setSelectedTerminated,
    companiesLoading = false,
    // When true, SearchBar will not restrict companies to adminBranchId
    ignoreAdminBranchFilter = false,
    showCountriesSelect = false,
    selectedCountry,
    setSelectedCountry,
    countriesOptions = [],
    setCountriesOptions,
    showNomenclaturesSelect = false,
    selectedNomenclature,
    setSelectedNomenclature,
    nomenclaturesOptions = [],

    // Level selection props
    levelOptions = [],
    selectedLevel,
    setSelectedLevel,
    levelsLoading = false,

    // SearchBar custom props
    searchBarProps = {},

    // Navigation arrows props (optional)
    showNavigationArrows = false,
    showPositionIcon = false,
    showPositionIconInRows = false,
    showOrgUnitIcon = false,
    onPositionIconClick = null,
    onOrgUnitIconClick = null,
    onNavigateForward = null, // Handler for forward arrow click
    onNavigateBack = null, // Handler for back arrow click
    canNavigateBack = false, // Boolean to enable/disable back arrow
    showMiniEditIcon = false,
    onMiniEditIconClick = null,
}) {

    const { t } = useTranslation();

    return (
        <div className='users-container'>
            <div className="table-header">
                <div className="title-section">
                    {title ? <h2>{title}</h2> : null}
                </div>
                <div className="controls-section">
                    <div className="table-actions">
                        {showCustomSearch ? (
                            customSearchComponent
                        ) : (
                            <SearchBar
                                getData={getData}
                                searchOptions={searchOptions}
                                sortBy={sortBy}
                                searchQuery={searchQuery}
                                searchType={searchType}
                                setSearchQuery={setSearchQuery}
                                setSearchType={setSearchType}
                                handleSearchClick={handleSearchClick}
                                setpageNumber={setpageNumber}
                                // Add company selection props
                                showCompanySelect={showCompanySelect}
                                companySelectLabel={companySelectLabel}
                                showLevelSelect={showLevelSelect}
                                companyOptions={companyOptions}
                                selectedCompany={selectedCompany}
                                setSelectedCompany={setSelectedCompany}
                                selectedActive={selectedActive}
                                setSelectedActive={setSelectedActive}
                                selectedAdditionalContract={selectedAdditionalContract}
                                setSelectedAdditionalContract={setSelectedAdditionalContract}
                                companiesLoading={companiesLoading}
                                showActiveSelect={showActiveSelect}
                                showAdditionalContractSelect={showAdditionalContractSelect}
                                showTerminatedSelect={showTerminatedSelect}
                                selectedTerminated={selectedTerminated}
                                setSelectedTerminated={setSelectedTerminated}
                                showCountriesSelect={showCountriesSelect}
                                selectedCountry={selectedCountry}
                                setSelectedCountry={setSelectedCountry}
                                countriesOptions={countriesOptions}
                                setCountriesOptions={setCountriesOptions}
                                showNomenclaturesSelect={showNomenclaturesSelect}
                                selectedNomenclature={selectedNomenclature}
                                setSelectedNomenclature={setSelectedNomenclature}
                                nomenclaturesOptions={nomenclaturesOptions}
                                // Add level selection props
                                levelOptions={levelOptions}
                                levelsLoading={levelsLoading}
                                selectedLevel={selectedLevel}
                                setSelectedLevel={setSelectedLevel}
                                ignoreAdminBranchFilter={ignoreAdminBranchFilter}
                                // Spread custom searchBar props
                                {...searchBarProps}
                            />
                        )}
                    </div>
                    <div className='flex-row'>
                        {showPageSizeSelector && (
                            <PageSizeSelector
                                pageSize={pageSize}
                                setPageSize={setPageSize}
                                setpageNumber={setpageNumber}
                            />
                        )}
                    </div>
                </div>
            </div>
            {/* Employee Info  */}
            {employeeInfo && (
                <div className="flex-row">
                   <input
                        type="text"
                        className="small-input width-10"
                        value={employeeEmpId || ''}
                        readOnly
                    />
                   <input
                        type="text"
                        className="small-input width-30"
                        value={employeeFullName || ''}
                        readOnly
                    />
                   <select className="small-select width-60" disabled>
                        <option value="current">{employeeContractName || ''}</option>
                   </select>
                </div>
            )}
            {/* Action Buttons */}
            {actionButtons && (
                <div className="action-buttons">
                    {showPreviousPage && <PreviousPage />}

                    {actionButtons}
                </div>
            )}

            {/* Breadcrumb Navigation */}
            {showBreadcrumb && breadcrumbPath && breadcrumbPath.length > 0 && (
                <div className="breadcrumb-navigation" style={{ marginBottom: '15px' }}>
                    {breadcrumbPath.map((item, index) => (
                        <span key={item.id}>
                            {index > 0 && <span className="breadcrumb-separator"> / </span>}
                            <span
                                className="breadcrumb-item clickable"
                                onClick={() => onBreadcrumbClick && onBreadcrumbClick(item, index)}
                                title={`Go to ${item.title}`}
                            >
                                {item.title}
                            </span>
                        </span>
                    ))}
                </div>
            )}

            <div className={`table-responsive custom-table-container ${showMiniEditIcon ? 'has-mini-edit-icon' : ''}`}>
                <table className="table table-hover">
                    <thead>
                        {showPositionIcon && (
                            <tr>
                                <td>
                                    <div className="action-icon" onClick={(e) => {
                                        e.stopPropagation(); // Prevent row click
                                        if (onPositionIconClick) {
                                            onPositionIconClick();
                                        }
                                    }}>
                                        <img title={t('organizationalStructure.structuralGroups.secondaryTitle') || 'Positions'} src='/images/icons/position.png' alt='position' style={{ width: '30px', height: '30px' }} />
                                    </div>
                                </td>
                            </tr>
                        )}
                        {showOrgUnitIcon && (
                            <tr>
                                <td>
                                    <div className="action-icon" onClick={(e) => {
                                        e.stopPropagation(); // Prevent row click
                                        if (onOrgUnitIconClick) {
                                            onOrgUnitIconClick();
                                        }
                                    }}>
                                        <img title={t('organizationalStructure.structuralGroups.table.headers.backToStructuralGroups') || 'Back to Structural Groups'}
                                            src='/images/icons/orgUnit.png' alt='orgUnit' style={{ width: '30px', height: '30px' }} />
                                    </div>
                                </td>
                            </tr>
                        )}
                        <tr>
                            {(showPositionIcon || showPositionIconInRows) && (
                                <th className="position-icon-column" style={{ width: '100px', textAlign: 'center' }}>
                                </th>
                            )}
                            {showCheckbox && (
                                <th className="radio-column">
                                    <div className="checkbox-wrapper">
                                    </div>
                                </th>
                            )}
                            {showDeleteIcon && (
                                <th className="delete-icon-column">
                                    <div className="delete-icon-wrapper">
                                    </div>
                                </th>
                            )}
                            {showSelectIcon && (
                                <th className="select-icon-column">
                                    <div className="select-icon-wrapper">
                                        {selectIconTitle}
                                    </div>
                                </th>
                            )}
                            {showNavigationArrows && (
                                <th className="navigation-arrows-column" style={{ width: '100px', textAlign: 'center' }}>
                                    {t('common.navigation') || 'Navigation'}
                                </th>
                            )}
                            {columns.map((column) => (
                                <th
                                    key={column.key}
                                    onClick={() => column.sortable && handleSort(column.key)}
                                    className={column.sortable ? 'sortable-header' : ''}
                                >
                                    {column.label}
                                    {column.sortable && sortBy === column.key && (
                                        <span className="sort-indicator">
                                            {sortDir === 'asc' ? ' ↑' : ' ↓'}
                                        </span>
                                    )}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={(showCheckbox ? 1 : 0) + (showSelectIcon ? 1 : 0) + (showDeleteIcon ? 1 : 0) + (showNavigationArrows ? 1 : 0) + ((showPositionIcon || showPositionIconInRows) ? 1 : 0) + columns.length} className="text-center">Loading...</td>
                            </tr>
                        ) : error ? (
                            <tr>
                                <td colSpan={(showCheckbox ? 1 : 0) + (showSelectIcon ? 1 : 0) + (showDeleteIcon ? 1 : 0) + (showNavigationArrows ? 1 : 0) + ((showPositionIcon || showPositionIconInRows) ? 1 : 0) + columns.length} className="text-center text-danger">{error}</td>
                            </tr>
                        ) : data?.length === 0 ? (
                            <tr>
                                <td colSpan={(showCheckbox ? 1 : 0) + (showSelectIcon ? 1 : 0) + (showDeleteIcon ? 1 : 0) + (showNavigationArrows ? 1 : 0) + ((showPositionIcon || showPositionIconInRows) ? 1 : 0) + columns.length} className="text-center">{t('common.dataNotFound')}</td>
                            </tr>
                        ) : (
                            (data?.content || data)?.map((item, index) => (
                                <tr
                                    key={item.id || index}
                                    onClick={() => handleRowClick ? handleRowClick(index) : (handleCheckboxChange ? handleCheckboxChange(index) : null)}
                                    className={(() => {
                                        const itemId = checkboxField ? item[checkboxField] : (item.branchId || item.id || item.codeIso || item.index);
                                        const isSelected = selectedData === itemId ? 'selected-row' : '';
                                        const withMiniEdit = showMiniEditIcon ? 'mini-edit-row' : '';
                                        return `${isSelected} ${withMiniEdit}`.trim();
                                    })()}
                                    style={{ cursor: handleRowClick || handleCheckboxChange ? 'pointer' : 'default' }}
                                >
                                    {showCheckbox && (
                                        <td
                                            className="radio-column"
                                            onClick={(e) => {
                                                e.stopPropagation();  // ← Stop bubbling to <tr>
                                                if (handleCheckboxChange) {
                                                    handleCheckboxChange(index);  // ← Call checkbox change
                                                }
                                            }}
                                        >
                                            <div className="checkbox-wrapper">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedData === (checkboxField ? item[checkboxField] : (item.branchId || item.id || item.codeIso))}
                                                    onChange={() => handleCheckboxChange && handleCheckboxChange(index)}
                                                    onClick={(e) => e.stopPropagation()}  // Keep this too for double safety
                                                />
                                            </div>
                                        </td>
                                    )}
                                    {showDeleteIcon && (
                                        <td
                                            className="delete-icon-column"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <div className="delete-icon-wrapper">
                                                {(!shouldShowDeleteIcon || shouldShowDeleteIcon(item)) && (
                                                    <img
                                                        src='/images/icons/delete.png'
                                                        alt='delete'
                                                        title={t('common.remove')}
                                                        style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (onDeleteIconClick) {
                                                                onDeleteIconClick(item, index, e);
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    )}
                                    {showSelectIcon && (
                                        <td className="select-icon-column">
                                            <div className="select-icon-wrapper">
                                                <button
                                                    type="button"
                                                    className={`select-icon-btn ${selectedRecord === (selectIconField ? item[selectIconField] : (item.id || item.branchId || item.codeIso)) ? 'selected' : ''}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (onSelectRecord) {
                                                            onSelectRecord(item, index);
                                                        }
                                                    }}
                                                    title={selectIconTitle}
                                                >
                                                    ✓
                                                </button>
                                            </div>
                                        </td>
                                    )}
                                    {showNavigationArrows && (
                                        <td className="navigation-arrows-column" style={{ textAlign: 'center' }}>
                                            <div style={{ display: 'flex', gap: '5px', justifyContent: 'center', alignItems: 'center' }}>
                                                <button
                                                    className="icon-button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (onNavigateForward) {
                                                            onNavigateForward(item, index);
                                                        }
                                                    }}
                                                    title={t('common.goDeeper')}
                                                    style={{ padding: '2px 6px' }}
                                                >
                                                    <img src='/images/icons/arrow.png' alt='arrow' style={{ width: '20px' }} />
                                                </button>
                                                <button
                                                    className={`icon-button ${!canNavigateBack ? 'disabled-link' : ''}`}
                                                    disabled={!canNavigateBack}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (onNavigateBack) {
                                                            onNavigateBack();
                                                        }
                                                    }}
                                                    title={t('common.goBack')}
                                                    style={{ padding: '2px 6px' }}
                                                >
                                                    <img src='/images/icons/arrow.png' alt='back' style={{ width: '20px', transform: 'rotate(180deg)' }} />
                                                </button>
                                            </div>
                                        </td>
                                    )}
                                    {showPositionIcon && !showPositionIconInRows && (
                                        <td className="position-icon-column" style={{ textAlign: 'center' }}>
                                            {/* Empty cell to maintain column alignment */}
                                        </td>
                                    )}
                                    {showPositionIconInRows && (
                                        (<td className="position-icon-column" style={{ textAlign: 'center' }}>
                                            <div className="action-icon" style={{ height: "20px" }} onClick={(e) => {
                                                e.stopPropagation();
                                                if (onPositionIconClick) {
                                                    onPositionIconClick(item, index);
                                                }
                                            }}>
                                                <img title={t('common.choose') || 'Choose'} src='/images/icons/downArrow.png' alt='position' style={{ width: '20px', height: '20px' }} />
                                            </div>
                                        </td>
                                        ))}
                                    {columns.map((column) => (
                                        <td key={column.key}>
                                            {renderCell ? renderCell(item, column) : (
                                                column.render ? column.render(item) : (
                                                    column.renderStatusBadge ? (
                                                        <span className={`status-badge ${item[column.key] ? 'active' : 'inactive'}`}>
                                                            {item[column.key] ? t('common.yes') : t('common.no')}
                                                        </span>
                                                    ) : (
                                                        // Auto-format dates in Bulgarian format
                                                        (() => {
                                                            const cellValue = item[column.key];
                                                            const isDate = isDateValue(cellValue);
                                                            return isDate ? formatDate(cellValue) : cellValue;
                                                        })()
                                                    )
                                                )
                                            )}
                                        </td>
                                    ))}
                                    {showMiniEditIcon && (
                                        <td className="mini-edit-icon-column">
                                            <div
                                                className="mini-edit-icon-wrapper"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (onMiniEditIconClick) {
                                                        onMiniEditIconClick(item, index);
                                                    } else {
                                                        console.error('onMiniEditIconClick is not provided');
                                                    }
                                                }}
                                            >
                                                <img
                                                    src='/images/icons/edit-mini.png'
                                                    alt='edit'
                                                    title={t('common.edit')}
                                                />
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                {showPagination && (
                    <Pagination
                        pageNumber={pageNumber}
                        totalPages={totalPages}
                        totalElements={totalElements}
                        pageSize={pageSize}
                        setpageNumber={setpageNumber}
                    />
                )}
            </div>
        </div >
    );
}
